export interface TestingStep {
  id: string;
  order: number;
  title: string;
  type: 'dead' | 'live' | 'visual';
  description: string;
  procedure: string[];
  acceptanceCriteria: string;
  regulation: string;
  tip: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface TestingSection {
  id: string;
  title: string;
  icon: string;
  colour: string;
  bgColour: string;
  borderColour: string;
  summary: string;
  steps: TestingStep[];
}

export const TESTING_SECTIONS: TestingSection[] = [
  {
    id: 'visual_inspection',
    title: 'Visual Inspection',
    icon: '👁️',
    colour: 'text-blue-700',
    bgColour: 'bg-blue-50',
    borderColour: 'border-blue-200',
    summary: 'Visual inspection must be carried out before any testing. It checks that the installation has been erected in accordance with BS 7671 and that no damage has occurred during installation.',
    steps: [
      {
        id: 'vi1',
        order: 1,
        title: 'Check Connection of Conductors',
        type: 'visual',
        description: 'Verify all conductors are correctly connected, properly terminated, and that no bare conductors are exposed outside terminals.',
        procedure: [
          'Inspect all terminations for correct conductor insertion',
          'Check for no strand cut-back or damaged insulation at terminals',
          'Verify conductors are not over-tightened (crushing) or under-tightened (loose)',
          'Confirm correct conductor colours are used throughout'
        ],
        acceptanceCriteria: 'All conductors correctly terminated with no exposed bare conductors outside terminals.',
        regulation: 'BS 7671 Regulation 526.1',
        tip: 'Use a torch to inspect inside enclosures. Tug each conductor gently to check it is secure.'
      },
      {
        id: 'vi2',
        order: 2,
        title: 'Identification of Conductors',
        type: 'visual',
        description: 'Confirm all conductors are correctly identified using the correct colour coding or marking.',
        procedure: [
          'Check line conductors are brown (or red in old installations)',
          'Check neutral conductors are blue (or black in old installations)',
          'Check protective conductors are green-and-yellow',
          'Verify sleeving is applied to bare earth conductors'
        ],
        acceptanceCriteria: 'All conductors correctly identified per BS 7671 Table 51.',
        regulation: 'BS 7671 Regulation 514.3',
        tip: 'Green-and-yellow sleeving must be applied to bare CPC conductors at every termination point.'
      },
      {
        id: 'vi3',
        order: 3,
        title: 'Routing of Cables',
        type: 'visual',
        description: 'Verify cables are routed safely, protected from mechanical damage, and installed in prescribed zones where applicable.',
        procedure: [
          'Check cables in walls are in prescribed zones (within 150mm of top/corner or behind metal plate)',
          'Verify cables are not subject to mechanical stress or damage',
          'Check cables are supported at appropriate intervals',
          'Confirm cables are not run in areas of excessive heat or moisture without appropriate protection'
        ],
        acceptanceCriteria: 'All cables routed safely with appropriate protection and support.',
        regulation: 'BS 7671 Regulation 522.6',
        tip: 'Cables in stud walls must be in zones or protected by earthed metal conduit to prevent nail/screw damage.'
      }
    ]
  },
  {
    id: 'continuity',
    title: 'Continuity Tests',
    icon: '🔗',
    colour: 'text-green-700',
    bgColour: 'bg-green-50',
    borderColour: 'border-green-200',
    summary: 'Continuity tests are dead tests (supply isolated) that verify the integrity of protective conductors and ring final circuits. They must be performed before insulation resistance tests.',
    steps: [
      {
        id: 'ct1',
        order: 1,
        title: 'Continuity of Protective Conductors (CPCs)',
        type: 'dead',
        description: 'Test that all protective conductors (earth wires) are continuous from the main earthing terminal to every point in the installation.',
        procedure: [
          'Isolate the supply and prove dead',
          'Connect a long test lead from the main earthing terminal',
          'Use a low-resistance ohmmeter (wander lead method)',
          'Test at each accessory: measure resistance between CPC terminal and main earth',
          'Record R2 values for each circuit'
        ],
        acceptanceCriteria: 'Resistance should be low (typically <1Ω for short runs). Any open circuit (infinite resistance) is a fail.',
        regulation: 'BS 7671 Regulation 612.2',
        tip: 'Null the test leads before measuring to remove lead resistance from your readings.'
      },
      {
        id: 'ct2',
        order: 2,
        title: 'Continuity of Ring Final Circuit Conductors',
        type: 'dead',
        description: 'Verify the ring final circuit is a complete ring with no breaks, spurs incorrectly wired as rings, or interconnected rings.',
        procedure: [
          'Disconnect the ring at the consumer unit',
          'Step 1: Measure end-to-end resistance of each conductor (L, N, E) — record as r1, rn, r2',
          'Step 2: Cross-connect L of one end to N of other end; measure at each socket — values should be approximately r1/4 + rn/4 ± 10%',
          'Step 3: Cross-connect L to E; measure r1+r2 at each socket — values should be uniform',
          'Compare measured values to verify ring integrity'
        ],
        acceptanceCriteria: 'End-to-end values consistent. Socket readings uniform (within 10% of r1+r2 / 4). No open circuits.',
        regulation: 'BS 7671 Regulation 612.2.2',
        tip: 'If socket readings vary significantly, suspect a broken ring or an incorrectly wired spur.'
      },
      {
        id: 'ct3',
        order: 3,
        title: 'Continuity of Main Protective Bonding',
        type: 'dead',
        description: 'Verify main protective bonding conductors are continuous between extraneous conductive parts and the main earthing terminal.',
        procedure: [
          'Identify all main bonding conductors (gas, water, structural steel)',
          'Use low-resistance ohmmeter',
          'Measure resistance between each extraneous conductive part and the main earthing terminal',
          'Record all values'
        ],
        acceptanceCriteria: 'Low resistance (typically <0.05Ω). Any open circuit is a fail.',
        regulation: 'BS 7671 Regulation 612.2.1',
        tip: 'Bonding clamps must be on the consumer side of the meter/stop cock and labelled "Safety Electrical Connection — Do Not Remove".'
      }
    ]
  },
  {
    id: 'insulation_resistance',
    title: 'Insulation Resistance',
    icon: '🛡️',
    colour: 'text-amber-700',
    bgColour: 'bg-amber-50',
    borderColour: 'border-amber-200',
    summary: 'Insulation resistance tests verify that the insulation between conductors and between conductors and earth is adequate. These are dead tests using DC voltage.',
    steps: [
      {
        id: 'ir1',
        order: 1,
        title: 'Preparation for IR Testing',
        type: 'dead',
        description: 'Before applying test voltage, prepare the installation to prevent damage to sensitive equipment.',
        procedure: [
          'Isolate the supply and prove dead',
          'Disconnect or short-circuit electronic equipment (dimmers, RCDs, SPDs, electronic ballasts)',
          'Switch all circuit breakers ON',
          'Switch all switches to ON position',
          'Remove or disconnect lamps and sensitive loads'
        ],
        acceptanceCriteria: 'All electronic equipment disconnected. All switches in ON position.',
        regulation: 'BS 7671 Regulation 612.3',
        tip: 'Failure to disconnect electronic equipment can cause permanent damage. Always check for dimmers, smart switches, and surge protection devices.'
      },
      {
        id: 'ir2',
        order: 2,
        title: 'Insulation Resistance Test (L-N to Earth)',
        type: 'dead',
        description: 'Apply 500V DC between all live conductors connected together and earth to test insulation integrity.',
        procedure: [
          'Connect L and N conductors together at the consumer unit',
          'Apply 500V DC between the L+N conductors and earth (CPC)',
          'Hold for at least 1 minute',
          'Record the reading in MΩ',
          'Test each circuit individually if the overall reading is below 1MΩ'
        ],
        acceptanceCriteria: 'Minimum 1 MΩ for circuits up to 500V nominal (BS 7671 Table 61).',
        regulation: 'BS 7671 Table 61',
        tip: 'A reading of infinity (>200MΩ) is ideal. Readings just above 1MΩ should be investigated — moisture or damaged insulation may be present.'
      },
      {
        id: 'ir3',
        order: 3,
        title: 'Insulation Resistance Test (L to N)',
        type: 'dead',
        description: 'Test insulation between line and neutral conductors.',
        procedure: [
          'Disconnect L and N from each other',
          'Apply 500V DC between L and N conductors',
          'Hold for at least 1 minute',
          'Record the reading in MΩ'
        ],
        acceptanceCriteria: 'Minimum 1 MΩ.',
        regulation: 'BS 7671 Table 61',
        tip: 'This test is particularly important for two-way switching circuits where the switch wire may be close to other conductors.'
      }
    ]
  },
  {
    id: 'polarity',
    title: 'Polarity Test',
    icon: '⚡',
    colour: 'text-purple-700',
    bgColour: 'bg-purple-50',
    borderColour: 'border-purple-200',
    summary: 'Polarity testing confirms that all single-pole devices (switches, fuses, MCBs) are connected in the line conductor only, and that socket outlets are correctly wired.',
    steps: [
      {
        id: 'pt1',
        order: 1,
        title: 'Dead Polarity Test',
        type: 'dead',
        description: 'Verify polarity using a continuity tester before energising the installation.',
        procedure: [
          'Isolate supply and prove dead',
          'Connect a long test lead to the line terminal at the consumer unit',
          'At each accessory, check continuity between the line terminal and the test lead',
          'Verify the line terminal of each socket outlet is connected to the line conductor',
          'Check all switches interrupt the line conductor'
        ],
        acceptanceCriteria: 'Continuity confirmed between line conductor and line terminal at every accessory.',
        regulation: 'BS 7671 Regulation 612.6',
        tip: 'Pay special attention to lighting circuits — a common mistake is connecting the switch in the neutral conductor.'
      },
      {
        id: 'pt2',
        order: 2,
        title: 'Live Polarity Verification',
        type: 'live',
        description: 'After energising, verify correct polarity at socket outlets using a socket tester or approved voltage indicator.',
        procedure: [
          'Energise the installation',
          'Use an approved socket tester or voltage indicator',
          'Test each socket outlet for correct polarity',
          'Verify line is at the correct terminal (right-hand slot on BS 1363 socket)',
          'Check for any reversed polarity indications'
        ],
        acceptanceCriteria: 'All socket outlets show correct polarity. No reversed polarity.',
        regulation: 'BS 7671 Regulation 612.6',
        tip: 'A socket tester with LED indicators is a quick way to check polarity, earth continuity, and wiring faults simultaneously.'
      }
    ]
  },
  {
    id: 'earth_fault_loop',
    title: 'Earth Fault Loop Impedance',
    icon: '🔄',
    colour: 'text-red-700',
    bgColour: 'bg-red-50',
    borderColour: 'border-red-200',
    summary: 'Earth fault loop impedance (Zs) testing verifies that in the event of a fault, sufficient current will flow to operate the protective device within the required disconnection time.',
    steps: [
      {
        id: 'efli1',
        order: 1,
        title: 'Measure External Earth Fault Loop Impedance (Ze)',
        type: 'live',
        description: 'Measure the impedance of the supply earth fault loop external to the installation.',
        procedure: [
          'Energise the installation',
          'Measure Ze at the origin of the installation (consumer unit)',
          'Use an approved earth fault loop impedance tester',
          'Measure between line and earth at the supply terminals',
          'Record the Ze value'
        ],
        acceptanceCriteria: 'Ze should match the expected value for the earthing system type (e.g., TN-S ≤0.8Ω, TN-C-S ≤0.35Ω typical).',
        regulation: 'BS 7671 Regulation 612.9',
        tip: 'Ze can also be calculated from the supply network data provided by the DNO if direct measurement is not possible.'
      },
      {
        id: 'efli2',
        order: 2,
        title: 'Measure Zs at Circuit Extremities',
        type: 'live',
        description: 'Measure the total earth fault loop impedance at the furthest point of each circuit.',
        procedure: [
          'Energise the installation',
          'Test at the furthest point of each circuit (last socket, last light fitting)',
          'Measure Zs between line and earth',
          'Record all Zs values',
          'Compare against maximum permitted Zs values in BS 7671 Tables 41.2–41.4'
        ],
        acceptanceCriteria: 'Measured Zs must not exceed the maximum values in BS 7671 for the protective device type and rating.',
        regulation: 'BS 7671 Tables 41.2, 41.3, 41.4',
        tip: 'Remember to apply a temperature correction factor if testing at ambient temperatures significantly different from 20°C. Multiply measured Zs by 0.8 as a practical check against table values.'
      },
      {
        id: 'efli3',
        order: 3,
        title: 'Calculate from R1+R2',
        type: 'dead',
        description: 'Alternatively, calculate Zs by adding Ze to the measured R1+R2 values from continuity testing.',
        procedure: [
          'Obtain Ze from live measurement or DNO data',
          'Obtain R1+R2 from continuity test results',
          'Calculate: Zs = Ze + (R1+R2)',
          'Apply temperature correction: multiply R1+R2 by 1.2 for 70°C PVC cables',
          'Compare calculated Zs against maximum permitted values'
        ],
        acceptanceCriteria: 'Calculated Zs must not exceed maximum permitted values in BS 7671.',
        regulation: 'BS 7671 Regulation 612.9',
        tip: 'This method avoids the need for live testing at every point and is useful for confirming results from live measurements.'
      }
    ]
  },
  {
    id: 'rcd_testing',
    title: 'RCD Testing',
    icon: '🔌',
    colour: 'text-teal-700',
    bgColour: 'bg-teal-50',
    borderColour: 'border-teal-200',
    summary: 'RCD testing verifies that residual current devices operate correctly within the required time limits to provide protection against electric shock and fire.',
    steps: [
      {
        id: 'rcd1',
        order: 1,
        title: 'RCD Test at 50% Rated Current (½× In)',
        type: 'live',
        description: 'Test that the RCD does NOT trip at half the rated residual current — this confirms it is not over-sensitive.',
        procedure: [
          'Energise the installation',
          'Connect RCD tester to a socket on the RCD-protected circuit',
          'Apply test current at 50% of rated residual current (e.g., 15mA for a 30mA RCD)',
          'Verify the RCD does NOT trip',
          'Record the result'
        ],
        acceptanceCriteria: 'RCD must NOT trip at ½× In.',
        regulation: 'BS 7671 Regulation 612.13',
        tip: 'If the RCD trips at ½× In, it is over-sensitive and should be replaced.'
      },
      {
        id: 'rcd2',
        order: 2,
        title: 'RCD Test at 100% Rated Current (1× In)',
        type: 'live',
        description: 'Test that the RCD trips within 300ms at the rated residual current.',
        procedure: [
          'Apply test current at 100% of rated residual current (e.g., 30mA for a 30mA RCD)',
          'Measure the trip time',
          'Record the trip time in milliseconds',
          'Repeat test with reversed polarity (×2 button on tester)'
        ],
        acceptanceCriteria: 'RCD must trip within 300ms at 1× In (30mA for a standard 30mA RCD).',
        regulation: 'BS 7671 Table 3A (Guidance Note 3)',
        tip: 'Test in both half-cycles (positive and negative) as RCDs can behave differently. Some testers have a ×2 button for this.'
      },
      {
        id: 'rcd3',
        order: 3,
        title: 'RCD Test at 500% Rated Current (5× In)',
        type: 'live',
        description: 'Test that the RCD trips within 40ms at five times the rated residual current.',
        procedure: [
          'Apply test current at 500% of rated residual current (e.g., 150mA for a 30mA RCD)',
          'Measure the trip time',
          'Record the trip time in milliseconds',
          'Verify result is within 40ms'
        ],
        acceptanceCriteria: 'RCD must trip within 40ms at 5× In.',
        regulation: 'BS 7671 Table 3A (Guidance Note 3)',
        tip: 'The 5× In test is the most critical for shock protection. A trip time exceeding 40ms is a fail and the RCD must be replaced.'
      },
      {
        id: 'rcd4',
        order: 4,
        title: 'RCD Functional Test (Test Button)',
        type: 'live',
        description: 'Press the integral test button to verify the RCD trips mechanically.',
        procedure: [
          'With the installation energised and RCD in the ON position',
          'Press the TEST button on the RCD',
          'Verify the RCD trips (handle moves to OFF or mid position)',
          'Reset the RCD',
          'Advise the user to carry out this test quarterly'
        ],
        acceptanceCriteria: 'RCD must trip when the test button is pressed.',
        regulation: 'BS 7671 Regulation 514.12.2',
        tip: 'The test button only tests the mechanical operation, not the trip time. Always use an RCD tester for full verification.'
      }
    ]
  }
];

export const SEQUENCE_QUIZ: QuizQuestion[] = [
  {
    id: 'sq1',
    question: 'What is the correct order for initial verification testing?',
    options: [
      'Insulation resistance → Continuity → Polarity → Zs → RCD',
      'Continuity → Insulation resistance → Polarity → Zs → RCD',
      'Polarity → Continuity → Insulation resistance → Zs → RCD',
      'Zs → RCD → Continuity → Insulation resistance → Polarity'
    ],
    correctIndex: 1,
    explanation: 'Dead tests first: Continuity → Insulation resistance → Polarity (dead). Then live tests: Polarity (live verification) → Earth fault loop impedance (Zs) → RCD testing.'
  },
  {
    id: 'sq2',
    question: 'Why must continuity tests be performed before insulation resistance tests?',
    options: [
      'Continuity tests are quicker to perform',
      'Insulation resistance test voltage could damage the continuity tester',
      'Continuity tests verify the circuit is safe to apply IR test voltage to',
      'There is no specific reason — either order is acceptable'
    ],
    correctIndex: 2,
    explanation: 'Continuity tests confirm the protective conductors are intact before applying high DC voltage. An open CPC during IR testing could leave exposed metalwork at dangerous potential.'
  },
  {
    id: 'sq3',
    question: 'Which tests are "dead tests" (supply must be isolated)?',
    options: [
      'Earth fault loop impedance and RCD testing',
      'Continuity and insulation resistance tests',
      'Polarity (live) and RCD testing',
      'All tests require the supply to be isolated'
    ],
    correctIndex: 1,
    explanation: 'Continuity and insulation resistance tests are dead tests — the supply must be isolated and proved dead before carrying them out. Zs and RCD tests are live tests.'
  },
  {
    id: 'sq4',
    question: 'What must you do before applying 500V DC for an insulation resistance test?',
    options: [
      'Switch all MCBs off',
      'Disconnect electronic equipment and short-circuit sensitive components',
      'Connect all loads to ensure a complete circuit',
      'Measure the earth fault loop impedance first'
    ],
    correctIndex: 1,
    explanation: 'Electronic equipment (dimmers, RCDs, SPDs, electronic ballasts) must be disconnected or short-circuited before applying 500V DC to prevent permanent damage.'
  },
  {
    id: 'sq5',
    question: 'A 30mA RCD takes 380ms to trip at 1× In (30mA). What is the correct action?',
    options: [
      'This is acceptable — the limit is 500ms',
      'Record the result and inform the client it is borderline',
      'The RCD has failed — it must be replaced',
      'Repeat the test three times and take the average'
    ],
    correctIndex: 2,
    explanation: 'A 30mA RCD must trip within 300ms at 1× In. A trip time of 380ms exceeds this limit — the RCD has failed the test and must be replaced before the installation is put into service.'
  }
];
